import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import ReactNativeInstall from "../../integrate/_snippets/install-react-native.mdx";
export const EnableSessionReplayDark = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-dark-mode.png";
export const EnableSessionReplayLight = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-light-mode.png";
export const _frontmatter = {
  "title": "React Native session replay"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const ProductScreenshot = makeShortcode("ProductScreenshot");
const layoutProps = {
  EnableSessionReplayDark,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "step-one-add-posthog-to-your-app"
    }}>{`Step one: Add PostHog to your app`}</h2>


    <ReactNativeInstall mdxType="ReactNativeInstall" />
    <blockquote>
      <p parentName="blockquote">{`Requires PostHog React Native SDK version >= `}<a parentName="p" {...{
          "href": "https://github.com/PostHog/posthog-js/releases"
        }}>{`3.2.0`}</a>{`, and it's recommended to always use `}<a parentName="p" {...{
          "href": "/docs/sdk-doctor"
        }}>{`the latest version`}</a>{`.`}</p>
    </blockquote>
    <h2 {...{
      "id": "step-two-install-the-session-replay-plugin"
    }}>{`Step two: Install the session replay plugin.`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`yarn add posthog-react-native-session-replay
# or npm
npm i -s posthog-react-native-session-replay
`}</code></pre>
    <h2 {...{
      "id": "step-three-enable-session-recordings-in-your-project-settings"
    }}>{`Step three: Enable session recordings in your project settings`}</h2>
    <p>{`Enable session recordings in your PostHog `}<a parentName="p" {...{
        "href": "https://app.posthog.com/project/settings"
      }}>{`Project Settings`}</a>{`.`}</p>
    <ProductScreenshot imageLight={EnableSessionReplayLight} imageDark={EnableSessionReplayDark} alt="Enable session recordings in your PostHog'" classes="rounded" mdxType="ProductScreenshot" />
    <h2 {...{
      "id": "step-four-configure-replay-settings"
    }}>{`Step four: Configure replay settings`}</h2>
    <p>{`Add `}<inlineCode parentName="p">{`enableSessionReplay: true`}</inlineCode>{` to your PostHog configuration alongside any of your other configuration options:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`export const posthog = new PostHog(
  '<ph_project_api_key>',
  {
    // Enable session recording. Requires enabling in your project settings as well.
    // Default is false.
    enableSessionReplay: true,
    sessionReplayConfig: {
      // Whether text and text input fields are masked. Default is true.
      // Password inputs are always masked regardless
      maskAllTextInputs: true,
      // Whether images are masked. Default is true.
      maskAllImages: true,
      // Enable masking of all sandboxed system views like UIImagePickerController, PHPickerViewController and CNContactPickerViewController. Default is true.
      // iOS only
      maskAllSandboxedViews: true,
      // Capture logs automatically. Default is true.
      // Android only (Native Logcat only)
      captureLog: true,
      // Whether network requests are captured in recordings. Default is true
      // Only metric-like data like speed, size, and response code are captured.
      // No data is captured from the request or response body.
      // iOS only
      captureNetworkTelemetry: true,
      // Throttling delay used to reduce the number of snapshots captured and reduce performance impact
      // The lower the number more snapshots will be captured but higher the performance impact
      // Default is 1000ms
      throttleDelayMs: 1000,
    },
  },
);
`}</code></pre>
    <p>{`Or using the `}<inlineCode parentName="p">{`PostHogProvider`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`<PostHogProvider
        apiKey="<ph_project_api_key>"
        options={{
          enableSessionReplay: true,
          ...
        }}
      >
`}</code></pre>
    <h2 {...{
      "id": "expo"
    }}>{`Expo`}</h2>
    <p>{`Mobile Session Replay for Expo works normally, but prefer the `}<inlineCode parentName="p">{`expo`}</inlineCode>{` tools for installing and running your project.`}</p>
    <p>{`Install the session replay plugin.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npx expo install posthog-react-native-session-replay
`}</code></pre>
    <p>{`Expo Go isn't supported since it uses Native plugins, please use development build.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`# Run Android development build 
npx expo run:android --no-build-cache --device

# Run iOS development build 
npx expo run:ios --no-build-cache --device
`}</code></pre>
    <h2 {...{
      "id": "limitations"
    }}>{`Limitations`}</h2>
    <ul>
      <li parentName="ul">{`On Android, requires API >= 26.`}</li>
      <li parentName="ul">{`On iOS, minimum deployment target is `}<a parentName="li" {...{
          "href": "/docs/libraries/ios"
        }}>{`iOS13`}</a></li>
      <li parentName="ul">{`Keyboard is not supported. A placeholder will be shown.`}</li>
      <li parentName="ul">{`Wireframe mode isn't supported, only screenshot mode.`}</li>
      <li parentName="ul">{`Expo Go isn't supported since it uses Native plugins, please use development build.`}</li>
      <li parentName="ul">{`Masking individual view components within a WebView isn't supported, if your WebView displays sensitive view components, you've to `}<a parentName="li" {...{
          "href": "/docs/session-replay/privacy?tab=React+Native"
        }}>{`mask`}</a>{` the whole WebView.`}</li>
    </ul>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">{`Run a clean build if you experience issues such as `}<inlineCode parentName="li">{`The package 'posthog-react-native-session-replay' doesn't seem to be linked`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`Update your SDK and iOS Pods.`}</li>
      <li parentName="ul">{`If you have enabled session replay using feature flags, the flags are evaluated on the device once the PostHog SDK starts as early as possible. The SDK might be using the cached flags from the previous SDK start. If you have changed the flag or its condition, kill and reopen the app to force a new SDK start at least once.`}
        <ul parentName="li">
          <li parentName="ul">{`This will also happen in production, and you might experience some delay for the new flag/conditions to take effect on your users. We're tracking this bug `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-ios/issues/263"
            }}>{`here`}</a>{`.`}</li>
          <li parentName="ul">{`Session replay feature flag evaluation does not capture `}<inlineCode parentName="li">{`$feature_flag_called`}</inlineCode>{` events, so the `}<inlineCode parentName="li">{`Usage`}</inlineCode>{` tab on the feature flag page won't show anything. We're tracking this feature request `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-android/issues/250"
            }}>{`here`}</a>{`.`}</li>
        </ul>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      